import type { AppointmentStatus } from "@planerius/contracts"
import type { ScheduleResult } from "@planerius/contracts/cabinet"

import { plural } from "../lib/plural"
import type { JournalCard, JournalDayModel } from "./journal-model"
import { buildJournalDay } from "./journal-model"

/**
 * 2-2.5 «Неделя» журнала — модель, отдельно от разметки.
 *
 * ЗАЧЕМ АДМИНИСТРАТОРУ НЕДЕЛЯ. День отвечает «что сейчас», неделя — «куда поставить».
 * Клиент в трубке говорит «когда-нибудь на следующей неделе, лучше вечером», и
 * администратор, листающий семь дней по одному, находит дыру на четвёртом листании,
 * а к тому времени клиент уже передумал.
 *
 * ДВА ПОДРЕЖИМА ОТВЕЧАЮТ НА РАЗНЫЕ ВОПРОСЫ, и это не украшение.
 * «Один мастер» — «когда свободна Анна»: клиент идёт к своему мастеру и другого
 * не хочет. «Загрузка салона» — «когда у нас вообще есть место»: клиенту всё равно,
 * кто его пострижёт, и салону важно продать пустой получас, а не конкретного мастера.
 * Одна сетка на оба вопроса отвечала бы плохо на каждый.
 *
 * НЕДЕЛЯ СТРОИТСЯ ПОВТОРНЫМ ВЫЗОВОМ РАСЧЁТА ДНЯ, а не своим расчётом. buildJournalDay
 * уже умеет то, что здесь легко сделать неправильно: день в зоне ФИЛИАЛА, окно дня,
 * паузы составных услуг, фильтр отменённых. Второй расчёт того же разошёлся бы с первым,
 * и «свободно» в дне не совпало бы со «свободно» в неделе — на соседних экранах.
 */

export type WeekMode = "master" | "load"

/** Шаг недельной сетки: получас. Мельче на 1200 px не читается, крупнее прячет дыры. */
export const WEEK_SLOT_MINUTES = 30

export interface WeekSlot {
  /**
   * В этот получас смены ни у кого нет, но кто-то занят.
   *
   * ЭТО НЕ «НЕ РАБОТАЕТ». Запись вне смены — штатное состояние продукта: сервер
   * предупреждает «у мастера нет смены», администратор подтверждает, и запись
   * создаётся. Заштрихованная клетка на таком получасе означала бы «продавать нечего»
   * рядом с клиентом, который уже придёт.
   */
  readonly outside?: boolean
  /** Минута от начала дня. */
  readonly minute: number
  /** Сколько мастеров работает в этот получас. 0 — никто. */
  readonly working: number
  /** Сколько из них занято. */
  readonly busy: number
  /** Доля занятых, 0…100. null — не работает никто: процента от нуля не бывает. */
  readonly percent: number | null
}

export interface WeekCard {
  readonly id: string
  readonly clientName: string
  readonly timeLabel: string
  /** Минута начала — для расстановки в сетке. */
  readonly startMinute: number
  readonly endMinute: number
  /** Запись начинается не на линии получаса: рамка пунктиром (макет 2-2.5). */
  readonly offGrid: boolean
  /** Отменённая рисуется приглушённо и не считается записью дня. */
  readonly status: AppointmentStatus
}

export interface WeekDay {
  readonly date: string
  /** «Пн 15» — как в макете. */
  readonly label: string
  readonly isToday: boolean
  readonly isClosed: boolean
  /** Работает ли в этот день хоть кто-нибудь. */
  readonly hasShifts: boolean
  /** Подпись под днём: зависит от подрежима. */
  readonly sub: string
  /** Получасы дня: только для «Загрузки салона». */
  readonly slots: readonly WeekSlot[]
  /** Записи выбранного мастера: только для «Одного мастера». */
  readonly cards: readonly WeekCard[]
  /** Смена выбранного мастера в этот день, минуты. null — выходной. */
  readonly shift: { readonly from: number; readonly to: number } | null
  readonly appointments: number
  readonly freeMinutes: number
}

export interface JournalWeek {
  readonly mode: WeekMode
  readonly days: readonly WeekDay[]
  /** Границы сетки: минимальное открытие и максимальное закрытие за неделю. */
  readonly openMinute: number
  readonly closeMinute: number
  readonly timeZone: string
  /** Выбранный мастер в режиме «Один мастер». null — не выбран. */
  readonly masterName: string | null
  readonly appointments: number
  readonly freeMinutes: number
}

/** Подписи дней недели. Первый элемент — воскресенье: так отвечает getUTCDay. */
const WEEKDAY_LABELS: readonly string[] = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"]

export function weekDayLabel(date: string): string {
  const parsed = new Date(`${date}T12:00:00Z`)
  return `${WEEKDAY_LABELS[parsed.getUTCDay()] ?? ""} ${String(parsed.getUTCDate())}`
}

function timeOfMinute(minute: number): string {
  const hours = Math.floor(minute / 60)
  const rest = minute % 60
  return `${String(hours).padStart(2, "0")}:${String(rest).padStart(2, "0")}`
}

/** «9 ч 05 мин», «40 мин». Часы салона читаются часами. */
export function hoursLabel(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes))
  const hours = Math.floor(safe / 60)
  const rest = safe % 60
  if (hours === 0) return `${String(rest)} мин`
  if (rest === 0) return `${String(hours)} ч`
  return `${String(hours)} ч ${String(rest).padStart(2, "0")} мин`
}

/**
 * Получасы одного дня: сколько мастеров работает и сколько из них занято.
 *
 * СЧИТАЮТСЯ МАСТЕРА, А НЕ МИНУТЫ. «Занято 3 из 6» — это ответ на вопрос «есть ли
 * к кому записать»: три свободных мастера в этот получас значит место есть, а «занято
 * 50 % времени» не значит ничего, потому что занятое время может быть у одного.
 *
 * ЗАНЯТ — ЭТО НЕ ТОЛЬКО «ЕСТЬ ЗАПИСЬ». Обед, «ушёл к врачу», «заблокировано: ремонт
 * кресла» и живое удержание с витрины занимают мастера ровно так же: записать в это
 * время нельзя. Считая занятость по одним записям, сетка показывала «1 из 6» там, где
 * свободен один мастер, — и администратор обещал клиенту по телефону время, которого
 * нет, а потом звонил обратно. Дневной экран всё это рисует, и расхождение между двумя
 * соседними экранами на одном и том же получасе — то самое, ради чего неделя считается
 * дневным расчётом.
 *
 * ЧЕГО ЗАНЯТОСТЬЮ НЕ СЧИТАЕМ. Системные блоки (буфер, уборка) принадлежат соседней
 * записи и уже посчитаны ею: сложив их отдельно, мы посчитали бы одного мастера дважды.
 * Истёкшее удержание не занимает ничего — оно и есть «время вернулось в продажу».
 * Отменённая запись освободила время: держать её в занятости значит советовать салону
 * не продавать свободный час.
 */
function slotsOf(day: JournalDayModel, from: number, to: number, now: number): readonly WeekSlot[] {
  const slots: WeekSlot[] = []
  for (let minute = from; minute < to; minute += WEEK_SLOT_MINUTES) {
    const end = minute + WEEK_SLOT_MINUTES
    const overlaps = (item: { readonly startMinute: number; readonly endMinute: number }): boolean =>
      item.startMinute < end && item.endMinute > minute

    let working = 0
    let busy = 0
    let outside = 0
    for (const column of day.columns) {
      const taken =
        column.cards.some((card) => card.status !== "cancelled" && overlaps(card)) ||
        column.busy.some((block) => !block.isSystem && overlaps(block)) ||
        column.holds.some((item) => overlaps(item) && Date.parse(item.expiresAt) > now)

      const onShift =
        column.workStartMinute !== null &&
        column.workEndMinute !== null &&
        // Смена должна покрывать получас целиком: мастер, у которого смена кончается
        // в 18:15, в получас 18:00–18:30 записать уже нельзя.
        column.workStartMinute <= minute &&
        column.workEndMinute >= end

      if (!onShift) {
        // Занятость без смены не теряется: её надо видеть, даже когда продавать нечего.
        if (taken) outside += 1
        continue
      }
      working += 1
      if (taken) busy += 1
    }
    slots.push({
      minute,
      working,
      busy,
      percent: working === 0 ? null : Math.round((busy / working) * 100),
      ...(working === 0 && outside > 0 ? { outside: true } : {}),
    })
  }
  return slots
}

function cardsOf(cards: readonly JournalCard[]): readonly WeekCard[] {
  return cards.map((card) => ({
    id: card.id,
    clientName: card.clientName,
    timeLabel: card.timeLabel,
    startMinute: card.startMinute,
    endMinute: card.endMinute,
    // Статус доезжает до разметки: отменённую надо рисовать приглушённо, как в дне,
    // а не наравне с живой — иначе мастер придёт к клиенту, который отменился.
    status: card.status,
    // Запись не на линии получаса — обычное дело при шаге сетки 5 или 15 минут,
    // и в недельной клетке её край иначе не отличить от соседней.
    offGrid: card.startMinute % WEEK_SLOT_MINUTES !== 0,
  }))
}

/**
 * Неделя журнала из одного ответа за семь дней.
 *
 * Границы сетки берутся по САМОМУ раннему открытию и САМОМУ позднему закрытию недели,
 * а не по фиксированным 9–21 из макета: салон с воскресной сменой до 23:00 иначе
 * потерял бы два часа записей, а салон, открытый с 11-ти, показывал бы два пустых часа.
 */
export function buildJournalWeek(input: {
  readonly result: ScheduleResult
  readonly dates: readonly string[]
  readonly today: string
  readonly mode: WeekMode
  /** Мастер для режима «Один мастер». */
  readonly employeeId: string | null
  readonly includeCancelled?: boolean
  /**
   * Момент, относительно которого удержание считается живым.
   *
   * ЗАДАЁТСЯ СНАРУЖИ, А НЕ БЕРЁТСЯ ИЗ ЧАСОВ УСТРОЙСТВА: расчёт обязан быть проверяемым,
   * а «сейчас» внутри функции означает проверку, которая ломается в полночь.
   */
  readonly now?: number
}): JournalWeek {
  const now = input.now ?? Date.now()
  const models = input.dates.map((date) =>
    buildJournalDay(input.result, date, { includeCancelled: input.includeCancelled ?? false }),
  )

  let openMinute = 24 * 60
  let closeMinute = 0
  for (const model of models) {
    if (model.window.isClosed) continue
    openMinute = Math.min(openMinute, model.window.openMinute)
    closeMinute = Math.max(closeMinute, model.window.closeMinute)
  }
  // Неделя целиком закрыта — сетку всё равно надо чем-то нарисовать: берём рабочий
  // день по умолчанию, чтобы шапки дней и штриховка «не работает» были видны.
  if (closeMinute <= openMinute) {
    openMinute = 9 * 60
    closeMinute = 21 * 60
  }
  // До целого получаса: сетка рисуется получасами, и половина строки внизу —
  // это половина ячейки, в которую нельзя попасть.
  openMinute = Math.floor(openMinute / WEEK_SLOT_MINUTES) * WEEK_SLOT_MINUTES
  closeMinute = Math.ceil(closeMinute / WEEK_SLOT_MINUTES) * WEEK_SLOT_MINUTES

  const masterName =
    input.employeeId === null
      ? null
      : (input.result.employees.find((item) => item.id === input.employeeId)?.displayName ?? null)

  const days: WeekDay[] = models.map((model, index) => {
    const date = input.dates[index] ?? model.date
    const working = model.columns.filter((column) => column.workStartMinute !== null)
    const own =
      input.employeeId === null
        ? null
        : (model.columns.find((column) => column.employee.id === input.employeeId) ?? null)

    const slots = input.mode === "load" ? slotsOf(model, openMinute, closeMinute, now) : []
    const cards = input.mode === "master" && own !== null ? cardsOf(own.cards) : []
    const shift =
      own === null || own.workStartMinute === null || own.workEndMinute === null
        ? null
        : { from: own.workStartMinute, to: own.workEndMinute }

    const dayBusy = slots.reduce((sum, slot) => sum + slot.busy, 0)
    const dayWorking = slots.reduce((sum, slot) => sum + slot.working, 0)

    /*
     * «СВОБОДНО N Ч» В НЕДЕЛЕ МАСТЕРА — ПРО МАСТЕРА, А НЕ ПРО САЛОН.
     *
     * Сводка дня считает свободное время ВСЕГО салона, и в подрежиме «Один мастер» эти
     * часы приписывались одному человеку: у Анны выходило «свободно 14 ч», потому что
     * свободен был Дмитрий. Мастер по такой строке решает, что у него полдня пусто.
     */
    const ownFree =
      own === null || own.workStartMinute === null || own.workEndMinute === null
        ? 0
        : Math.max(
            0,
            own.workEndMinute -
              own.workStartMinute -
              own.cards
                .filter((card) => card.status !== "cancelled")
                .reduce((sum, card) => sum + (card.endMinute - card.startMinute), 0),
          )

    return {
      date,
      label: weekDayLabel(date),
      isToday: date === input.today,
      isClosed: model.window.isClosed,
      hasShifts: working.length > 0,
      sub: daySub({
        mode: input.mode,
        isClosed: model.window.isClosed,
        hasShifts: working.length > 0,
        percent: dayWorking === 0 ? null : Math.round((dayBusy / dayWorking) * 100),
        masterName,
        shift,
      }),
      slots,
      cards,
      shift,
      // Отменённые не считаются записями ни в одном режиме: подпись «4 записи» рядом
      // с тремя живыми и одной отменённой спорит сама с собой.
      appointments:
        input.mode === "master"
          ? cards.filter((card) => card.status !== "cancelled").length
          : model.summary.appointments,
      freeMinutes: input.mode === "master" ? ownFree : model.summary.freeMinutes,
    }
  })

  return {
    mode: input.mode,
    days,
    openMinute,
    closeMinute,
    timeZone: input.result.branch.timezone,
    masterName,
    appointments: days.reduce((sum, day) => sum + day.appointments, 0),
    freeMinutes: days.reduce((sum, day) => sum + day.freeMinutes, 0),
  }
}

/**
 * Подпись под днём — дословно из макета 2-2.5.
 *
 * ПОРЯДОК ПРОВЕРОК ВАЖЕН. «Филиал закрыт» сильнее «никто не работает»: если салон
 * не работает, отсутствие смен — следствие, а не новость, и говорить про смены значит
 * послать управляющего искать в графике то, чего там и не должно быть.
 *
 * ПО ТОЙ ЖЕ ПРИЧИНЕ «никто не работает» СИЛЬНЕЕ «выходного Анны»: в неделе мастера
 * это разные новости — «отдыхает она одна» посылают править её график, а «смен нет
 * ни у кого» означает, что день не разложен вовсе. Проверка ведётся по сменам ВСЕГО
 * салона, а не выбранного мастера: иначе открытый день с другими мастерами на смене
 * подписывался бы «никто не работает», то есть неправдой.
 */
function daySub(input: {
  readonly mode: WeekMode
  readonly isClosed: boolean
  readonly hasShifts: boolean
  readonly percent: number | null
  readonly masterName: string | null
  readonly shift: { readonly from: number; readonly to: number } | null
}): string {
  if (input.isClosed) return "филиал закрыт"
  if (!input.hasShifts) return "никто не работает"
  if (input.mode === "master") {
    if (input.masterName === null) return ""
    const short = input.masterName.split(" ")[0] ?? input.masterName
    /*
     * ПАДЕЖА ЗДЕСЬ НЕТ НАМЕРЕННО. «Выходной Анна» — ошибка, «выходной Анны» требует
     * родительного падежа от произвольного имени, а склонять имена салона мы не умеем
     * и не должны: «Анна — выходной» верно для любого имени и читается так же.
     *
     * ЧАСЫ НЕ ОКРУГЛЯЮТСЯ ДО ЦЕЛОГО. Смена 9:30–20:15, подписанная «9–20», — это
     * полчаса, которых мастер не работает, и четверть часа, которую он работает
     * без подписи: по такой строке администратор поставит запись на 9:15.
     */
    return input.shift === null
      ? `${short} — выходной`
      : `${short} ${shortHour(input.shift.from)}–${shortHour(input.shift.to)}`
  }
  return input.percent === null ? "никто не работает" : `занято ${String(input.percent)} %`
}

/** «10» для ровного часа и «9:30» для получаса: подпись смены не врёт о минутах. */
function shortHour(minute: number): string {
  const hour = Math.floor(minute / 60)
  const rest = minute % 60
  return rest === 0 ? String(hour) : `${String(hour)}:${String(rest).padStart(2, "0")}`
}

/** Подписи часов слева: только на целом часе (макет 2-2.5). */
export function weekHourLabels(openMinute: number, closeMinute: number): readonly string[] {
  const out: string[] = []
  for (let minute = openMinute; minute < closeMinute; minute += WEEK_SLOT_MINUTES) {
    out.push(minute % 60 === 0 ? timeOfMinute(minute) : "")
  }
  return out
}

/** Итог недели под шапкой — дословно из макета, свой на каждый подрежим. */
export function weekSummary(week: JournalWeek): string {
  if (week.mode === "load") {
    return "Занято мастеров из работающих по получасам · насыщенное — дыр нет, пустое — можно продать · клик по ячейке открывает день"
  }
  if (week.masterName === null) {
    return "Выберите мастера, чтобы увидеть его неделю: «Загрузка салона» отвечает на другой вопрос — где вообще есть место"
  }
  const records = `${String(week.appointments)} ${plural(week.appointments, "запись", "записи", "записей")} на неделе`
  return `${week.masterName} · ${records} · свободно ${hoursLabel(week.freeMinutes)} · перенос между днями — только формой`
}

/**
 * Подсказка ячейки загрузки: «10:30: занято 3 из 6 мастеров».
 *
 * ВРЕМЯ НАЗЫВАЕТСЯ ВСЕГДА, В ТОМ ЧИСЛЕ В НЕРАБОЧЕМ ПОЛУЧАСЕ. Подсказка служит и именем
 * клетки для чтеца: «Не работает» без времени и дня незрячий слышит как имя кнопки,
 * и из семи сотен таких клеток не собирается ни одна мысль.
 */
export function slotTitle(slot: WeekSlot): string {
  if (slot.working === 0 && slot.outside === true) {
    return `${timeOfMinute(slot.minute)}: смены нет, но время занято`
  }
  if (slot.working === 0) return `${timeOfMinute(slot.minute)}: никто не работает`
  return `${timeOfMinute(slot.minute)}: занято ${String(slot.busy)} из ${String(slot.working)} ${plural(
    slot.working,
    "мастера",
    "мастеров",
    "мастеров",
  )}`
}

/**
 * «3/6» — занято из работающих.
 *
 * СВОБОДНЫЙ ПОЛУЧАС НЕ ПОДПИСЫВАЕТСЯ. Глаз ищет в сетке занятое, а «0/6» в каждой
 * свободной клетке — стена нулей на две трети сетки, сквозь которую занятое уже
 * не видно. Пусто и там, где никто не работает: смысл там другой, и его несёт
 * штриховка, а не число.
 */
export function slotText(slot: WeekSlot): string {
  if (slot.working === 0 && slot.outside === true) return "!"
  if (slot.working === 0 || slot.busy === 0) return ""
  return `${String(slot.busy)}/${String(slot.working)}`
}

/**
 * Ступень заливки ячейки по занятости — пороги из макета О-7 и 2-2.5.
 *
 * ЧЕТЫРЕ СТУПЕНИ, А НЕ ПЛАВНЫЙ ГРАДИЕНТ. Глаз ищет в сетке дыру, а не оттенок:
 * плавная шкала различима только рядом стоящими клетками, а «светлое — можно продать»
 * должно читаться через всю неделю.
 */
export function slotTone(slot: WeekSlot): "idle" | "low" | "mid" | "high" | "off" | "outside" {
  // Смены нет, а занятость есть: ни «продавать нечего», ни «можно продать».
  if (slot.working === 0 && slot.outside === true) return "outside"
  if (slot.working === 0) return "off"
  const percent = slot.percent ?? 0
  if (percent >= 85) return "high"
  if (percent >= 60) return "mid"
  if (percent >= 30) return "low"
  return "idle"
}

/**
 * Почему перетаскивание запрещено — дословно из макета 2-2.5.
 *
 * ЗАПРЕТ ОБЪЯСНЯЕТСЯ, А НЕ МОЛЧИТ. Перетаскивание — первое, что человек пробует
 * в сетке: если оно просто не работает, он решает, что интерфейс сломан, и пробует
 * ещё трижды. Цена ошибки здесь настоящая: клетки в неделе мелкие, и промах на одну
 * означает перенос клиента на чужой день.
 */
export const WEEK_DRAG_NOTE =
  "Чтобы перенести запись на другой день, откройте её и нажмите «Перенести». Перетаскивание между днями в неделе отключено — карточки мелкие, а цена ошибки высока."
